import { Inject, Injectable } from '@angular/core';
import { BlendPortfolioUtil } from '@ddt-web/blending';
import { ListingCategories } from '@ddt-web/common';
import { APP_STORE_CONFIG_TOKEN, ERROR_CODES, NilePaConstants } from '@ddt-web/config';
import { ConfigService, SharedConfigKey } from '@ddt-web/config-layer';
import { AnalysisParams, ApiStoreConfiguration, AppStoreConfig, DataServiceApiConstants, PasFeature, PasRcResolverService, PortfolioAnalysisParams, PositionDetails } from '@ddt-web/data-service';
import { AdditionalAnalysisParams, ApiParamPortfolio, WebUserDataConstants, WweApiParamPositionImpl, WweBenchmarkPosition } from '@ddt-web/domain';
import { ErrorObject, PasErrorPortfolioDetails, PasSecurityDetailsPartialError } from '@ddt-web/error-handler';
import { AssetResolveResponse, PasApiService } from '@ddt-web/pas';
import { PortfolioIdUtil } from '@ddt-web/portfolio-data';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { PortfolioSecurityIdentifierMappingUtil, SecurityIdentifiersMapRequest, SecurityResolutionDetailsRequest, WwePortfolioPosition, WwePosition, WweSecurityDetailsParams } from '@ddt-web/wealth-web';
import { EntityState } from '@ngrx/entity';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as uniqBy from 'lodash/uniqBy';
import * as values from 'lodash/values';
import { combineLatest, forkJoin, Observable, of, zip } from 'rxjs';
import { first, map, mergeMap, switchMap } from 'rxjs/operators';

import { PasPortfolioPositionsUtil } from '../../../utils/pas-portfolio-positions.util';
import { PortfolioPositionsUtil } from '../../../utils/portfolio-positions.util';
import { PasCustomParams } from '../../models/pas-processing.interface';
import { PasApiParamsService } from '../pas-api-params.service';
import { PasDataPortfolioAnalysisService } from '../pas-data-portfolio-analysis.service';

/**
 * Service that processes the PAS APIs covering asset-resolve API call for portfolio analysis workflows/ applications.
 * This is the default behaviour used via PasDataLoaderService.
 *
 * This supports:
 * - building the request body (if not provided)
 * - making the API call
 * - error handling based on the value of throwSecurityDetailsPartialPortfoliosErrors
 */
@Injectable()
export class PasApiProcessingService {

	constructor(
		protected pasDataPortfolioAnalysisService: PasDataPortfolioAnalysisService,
		protected apiParamService: PasApiParamsService, // can move the method to the normal API service
		protected storeService: StoreService,
		protected pasApiService: PasApiService,
		protected configService: ConfigService,
		protected applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		protected pasRcResolver: PasRcResolverService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig
	) { }

	public getPositionDetailsResponse(
		filteredIds: string[],
		throwSecurityDetailsPartialPortfoliosErrors: boolean,
		additionalAnalysisParams?: AdditionalAnalysisParams,
		customSecurityBody?: SecurityResolutionDetailsRequest,
		feature?: PasFeature,
		positionDetailsType: string = PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS,
		transientAnalysisId?: string
	): Observable<AssetResolveResponse> {
		const store = this.storeConfig[PositionDetails.SECURITY_DETAILS_STORE_MAPPING[positionDetailsType]] || this.storeConfig.positionDetails;

		return combineLatest([
			this.storeService.selectByIds<any>(store, filteredIds), // TO BE UPDATED
			this.storeService.selectAll(this.storeConfig.associatedPortfolios),
			this.storeService.selectAll(this.storeConfig.positionDetails)
		]).pipe(
			first(),
			switchMap(([storeResult, associatedPortfoliosResult, positionDetailsStore]) => {
				// Fire an api call to get the security details of every missing security within the given portfolios.
				const body =
					customSecurityBody ||
					this.buildSecurityDetailsParams(
						filteredIds,
						associatedPortfoliosResult,
						storeResult,
						additionalAnalysisParams,
						true,
						positionDetailsStore,
						transientAnalysisId
					);
				const positionDataFromApiCall = !isEmpty(body.securities)
					? this.fetchSecurityDetailsData(
						body,
						associatedPortfoliosResult,
						throwSecurityDetailsPartialPortfoliosErrors,
						additionalAnalysisParams,
						feature,
						filteredIds,
						positionDetailsType
					)
					: of(undefined);

				return positionDataFromApiCall.pipe(first());
			})
		);
	}

	public fetchSecurityDetailsData(
		body: SecurityResolutionDetailsRequest,
		associatedPortfoliosResult: EntityState<any>,
		throwSecurityDetailsPartialPortfoliosErrors: boolean,
		additionalAnalysisParams?: AdditionalAnalysisParams,
		feature?: PasFeature,
		ids?: string[],
		positionDetailsType: string = PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS
	): Observable<AssetResolveResponse> {
		const rcType = DataServiceApiConstants[positionDetailsType] ?? DataServiceApiConstants.SECURITY_DETAILS;
		const storeName = PositionDetails.SECURITY_DETAILS_STORE_MAPPING[positionDetailsType] || PasApiService.POSITION_DETAILS_CONFIG_NAME;

		return this.pasRcResolver.getRcForUserType(rcType).pipe(
			map((rc: string) => ({ body, rc })),
			// Map PAS securities Request to PAS asset resolve Request
			switchMap((requestParams: WweSecurityDetailsParams) => this.pasApiService.fetchSecurityDetails(requestParams, feature, storeName)),
			map(response => {
				if (throwSecurityDetailsPartialPortfoliosErrors && response?.errorCodes) {
					const associatedPortfolios = values(associatedPortfoliosResult.entities);
					const aggregatedSecurityKeys = Array.from(response.errorCodes.keys());
					const portfolioNames: PasErrorPortfolioDetails[] = associatedPortfolios
						.filter(portfolio => {
							if (ids?.includes(portfolio.id)) {
								// Filtering any portfolios that don't have erroring securities
								return Boolean(
									aggregatedSecurityKeys.find(aggregatedSecurityKey => {
										// If a position is found then the portfolio contains an failing security and needs to be resolved
										const children = get(portfolio, 'positions', get(portfolio, 'children', []));
										return Boolean(
											children.find(
												position =>
													// Checking that one of the error code keys matches any position inside the portfolio
													aggregatedSecurityKey ===
													PortfolioSecurityIdentifierMappingUtil.buildSecurityKey(position, additionalAnalysisParams?.identifierToBeUsedAsWweApiParamId)
											)
										);
									})
								);
							}
						})
						.map(portfolio => <PasErrorPortfolioDetails>{ id: portfolio.id, description: portfolio.description });

					this.setHoldingsError(portfolioNames.map(portfolio => portfolio.id));
					this.handleSecurityDetailsPartialErrors(
						new PasSecurityDetailsPartialError(response.errorCodes, portfolioNames, !!response.assets.size),
						storeName,
						feature
					);
				}
				return response;
			})
		);
	}

	private setHoldingsError(ids: string[]): void {
		if (ids?.length) {
			this.storeService
				.selectByIds(this.storeConfig.userPortfolios, ids)
				.pipe(first(userPortfolios => !userPortfolios.isLoading))
				.subscribe(userPortfolios => {
					const entities: any[] = Object.values(userPortfolios.entities);
					const portfoliosToUpdate = entities.filter(portfolio => !portfolio.hasHoldingsError).map(portfolio => ({ ...portfolio, ...{ hasHoldingsError: true } }));
					if (portfoliosToUpdate.length) {
						this.storeService.upsertMany(this.storeConfig.userPortfolios, portfoliosToUpdate);
					}
				});
		}
	}

	public buildSecurityDetailsParams(
		ids: string[],
		associatedPortfolios: any,
		portfolios: EntityState<WwePortfolioPosition>,
		additionalAnalysisParams?: AdditionalAnalysisParams,
		isFromAssociatedPortfoliosStore: boolean = true,
		positionDetailsStore?: any,
		transientAnalysisId?: string
	): SecurityResolutionDetailsRequest {
		const missingSecuritiesList = [];
		let isNoFundsAfterFilteringForSomePortfolios: boolean = false;

		ids.forEach(id => {
			if (isFromAssociatedPortfoliosStore) {
				const portfolio: WwePortfolioPosition = portfolios.entities[id];
				const associatedPortfolio = associatedPortfolios.entities[id];
				let enrichedPositions = [];

				if (associatedPortfolio) {

					this.apiParamService
						.flattenPortfolios([id], this.storeConfig, true)
						.pipe(first())
						.subscribe((flattenedPortfolios: Array<any>) => {
							const flattenedPortfolio = flattenedPortfolios[0];
							const associatedPortfoliosPositions = flattenedPortfolio.children || flattenedPortfolio.positions || [];
							const securityKeyIdentifier = PasPortfolioPositionsUtil.getSecurityKeyIdentifier(associatedPortfoliosPositions, additionalAnalysisParams);
							const positionDetailsEntities = positionDetailsStore.entities?.[transientAnalysisId ?? id];

							// Enrich associated portfolios children with PAS asset resolve response to identify assetType
							if (!isEmpty(positionDetailsEntities?.children)) {
								enrichedPositions = associatedPortfoliosPositions.map(associatedPortfoliosPosition => {
									const matchedPosition = this.mapPositionsViaSecurityKeyIdentifier(associatedPortfoliosPosition, positionDetailsEntities, securityKeyIdentifier);
									return { ...associatedPortfoliosPosition, ...matchedPosition };
								});
							}

							// Different children attributes for portfolios and engagements (due to apb)
							let associatedPortfolioChildren = enrichedPositions.length ? enrichedPositions : associatedPortfoliosPositions;
							const initialChildren = cloneDeep(associatedPortfolioChildren);

							if (additionalAnalysisParams?.securityRequestFilter) {
								associatedPortfolioChildren = associatedPortfolioChildren.filter(child => additionalAnalysisParams.securityRequestFilter(child));
							}

							// if no securities are valid after applying the security filter for the current portfolio, keep a track of that
							if (initialChildren.length && !associatedPortfolioChildren.length) {
								isNoFundsAfterFilteringForSomePortfolios = true;
							}

							// Find out if there are any missing securities and do a security api call to retrieve them
							const missingSecurityIdentifiers: Array<SecurityIdentifiersMapRequest> = associatedPortfolioChildren
								.filter(associatedPosition => {

									const securityFound = this.mapPositionsViaSecurityKeyIdentifier(associatedPosition, portfolio, securityKeyIdentifier); // position might be null for first time we run dirty partner portfolio
									return !securityFound;
								})
								.map(filteredPosition => {
									return this.buildPositionRequestFromIdentifier(filteredPosition, securityKeyIdentifier);
								});

							missingSecuritiesList.push.apply(missingSecuritiesList, missingSecurityIdentifiers);
						});
				}
			} else {
				const flattenedPortfolio = this.apiParamService.flattenACServerResponse(associatedPortfolios, id);
				const children = flattenedPortfolio['positions'] || [];

				const securityKeyIdentifier = PasPortfolioPositionsUtil.getSecurityKeyIdentifier(children, additionalAnalysisParams);

				const missingSecurityIdentifiers: Array<SecurityIdentifiersMapRequest> = children
					.map(filteredPosition => {
						return this.buildPositionRequestFromIdentifier(filteredPosition, securityKeyIdentifier);
					});
				missingSecuritiesList.push.apply(missingSecuritiesList, missingSecurityIdentifiers);
			}
		});

		// if no securities are valid overall for all analysed portfolio due to security filtering, throw error with NO_FUNDS_AFTER_FILTERING - it is up to the application to handle it
		if (isNoFundsAfterFilteringForSomePortfolios && isEmpty(missingSecuritiesList)) {
			throw new ErrorObject({ errorCodes: ERROR_CODES.WWE_CUSTOM_ERROR_CODES.NO_FUNDS_AFTER_FILTERING });
		}

		return {
			securities: uniqBy(missingSecuritiesList, 'id')
		} as SecurityResolutionDetailsRequest;
	}

	public handleSecurityDetailsPartialErrors(error, configName, feature?: PasFeature): void {
		this.storeService.upsertMany(configName, []);
		throw new ErrorObject(error, feature === PasFeature.TRANSIENT_ANALYSIS ? PasApiService.TRANSIENT_POSITION_DETAILS_CONFIG_NAME : configName);
	}

	// total-portfolios methods
	public setupAnalysisParamsSubscription(
		ids: string[],
		configArray: ApiStoreConfiguration[],
		associatedPortfolios: any,
		modelsConfig: any,
		additionalAnalysisParams: AdditionalAnalysisParams,
		pasCustomParams?: PasCustomParams
	): void {
		const generalAnalysisParamsObservable = pasCustomParams?.customPortfolioBody
			? of(pasCustomParams?.customPortfolioBody)
			: this.getAnalysisParamsArray(ids, modelsConfig, associatedPortfolios, additionalAnalysisParams, pasCustomParams);
		generalAnalysisParamsObservable.pipe(first()).subscribe(generalAnalysisParams => {
			this.pasDataPortfolioAnalysisService.loadPortfolioAnalysisData(configArray, generalAnalysisParams);
		});
	}

	public getAnalysisParamsArray(
		ids: any[],
		modelsConfig: any,
		associatedPortfolios: any = { entities: [] },
		additionalAnalysisParams: AdditionalAnalysisParams,
		pasCustomParams?: PasCustomParams,
		isBenchmarkPerPortfolio?: boolean
	): Observable<any> {
		const currency = this.resolveCurrency(additionalAnalysisParams.currency);
		const analysisParamsArray: Observable<PortfolioAnalysisParams>[] = this.getPortfolioAnalysisParam(
			ids, associatedPortfolios, modelsConfig, additionalAnalysisParams, currency, pasCustomParams, isBenchmarkPerPortfolio
		);

		return zip(...analysisParamsArray).pipe(first(), mergeMap(analysisParamsArr => {
			if (isBenchmarkPerPortfolio) {
				return forkJoin(ids.map(id => {
					const additionalAnalysisParam = {
						...additionalAnalysisParams,
						benchmark: additionalAnalysisParams.benchmark?.[id],
						rules: additionalAnalysisParams.rules?.[id],
						statuses: additionalAnalysisParams.statuses ?? null,
						configs: additionalAnalysisParams.configs?.[id]
					};
					const analysisParamsArray = analysisParamsArr.filter(analysisParam => analysisParam.uniqueId === id);
					return this.getAnalysisParamsWithBenchmark(associatedPortfolios, modelsConfig, analysisParamsArray, currency, additionalAnalysisParam);
				}));
			} else {
				return this.getAnalysisParamsWithBenchmark(associatedPortfolios, modelsConfig, analysisParamsArr, currency, additionalAnalysisParams);
			}
		}));
	}

	public getAnalysisParamsWithBenchmark(
		associatedPortfolios: any,
		modelsConfig: any,
		analysisParamsArr: PortfolioAnalysisParams[],
		currency: string,
		additionalAnalysisParams: AdditionalAnalysisParams
	): Observable<AnalysisParams> {
		let benchmarkApiParam: ApiParamPortfolio;
		let selectedBenchmark: string;

		if (Array.isArray(additionalAnalysisParams.benchmark)) {
			const benchmarkPositions: WweBenchmarkPosition[] = additionalAnalysisParams.benchmark;
			benchmarkApiParam = benchmarkPositions ?
				new ApiParamPortfolio(
					additionalAnalysisParams?.benchmark[0]?.id,
					PortfolioPositionsUtil.createWweParamPositions(benchmarkPositions, 'benchmark-'),
					ListingCategories.UNKNOWN,
					currency
				) : null;
		} else {
			const selectedPortfolioBenchmarkId = additionalAnalysisParams?.benchmark?.portfolioBenchmarkId;
			const benchmarkPosition: WweBenchmarkPosition = additionalAnalysisParams.benchmark;
			if (selectedPortfolioBenchmarkId) {
				selectedBenchmark = selectedPortfolioBenchmarkId;
				this.applicationStateStoreProxyService.updateAnalysisState({
					selectedCurrency: currency,
					selectedBenchmark: selectedBenchmark
				});
				return this.createPortfolioBenchmarkApiParam(selectedPortfolioBenchmarkId, associatedPortfolios, modelsConfig, currency).pipe(map(benchmarkApiParam => {
					return new AnalysisParams(
						analysisParamsArr,
						benchmarkApiParam,
						null,
						additionalAnalysisParams.simulatedPerformanceUserInputs,
						additionalAnalysisParams.configs
					);
				}));
			} else {
				benchmarkApiParam = additionalAnalysisParams.benchmark ?
					new ApiParamPortfolio(
						additionalAnalysisParams?.benchmark?.id,
						PortfolioPositionsUtil.createWweParamPositions([benchmarkPosition], 'benchmark-'),
						ListingCategories.USER_PORTFOLIO,
						currency
					) : null;
				selectedBenchmark = benchmarkPosition?.identifiers?.aladdinId;
			}

			this.applicationStateStoreProxyService.updateAnalysisState({
				selectedCurrency: currency,
				selectedBenchmark: selectedBenchmark
			});
		}

		return of(new AnalysisParams(
			analysisParamsArr,
			benchmarkApiParam,
			null,
			additionalAnalysisParams.simulatedPerformanceUserInputs,
			additionalAnalysisParams.configs
		));
	}

	public getPortfolioAnalysisParam(
		ids: any[],
		associatedPortfolios: any,
		modelsConfig: any,
		additionalAnalysisParams: AdditionalAnalysisParams,
		currency: string,
		pasCustomParams?: PasCustomParams,
		isBenchmarkPerPortfolio?: boolean
	): Observable<PortfolioAnalysisParams>[] {
		return ids.map(id => {
			if (modelsConfig?.[id]) {
				const model = associatedPortfolios.entities[id];
				const finalId = PortfolioIdUtil.isThirdPartyModel(id) && model ? model.tpmId : id;
				const modelPositions = [new WweApiParamPositionImpl(modelsConfig?.[finalId]?.aladdinPortfolioCode ?? finalId, { modelId: modelsConfig?.[finalId]?.aladdinPortfolioCode ?? finalId }, 100, 100)]; // do we need the value here as well?

				return of(new PortfolioAnalysisParams(id, null, modelPositions, null, currency));
			} else if (associatedPortfolios.entities[id]) {
				return this.apiParamService.flattenPortfolios([id], this.storeConfig).pipe(
					first(),
					map(portfolioParams => {
						const positions = PasPortfolioPositionsUtil.createWweParamPositions(
							portfolioParams[0]['positions'],
							'',
							additionalAnalysisParams?.identifierToBeUsedAsWweApiParamId
						);

						if (!currency) {
							throw new Error('Invalid currency found in the url queryParams');
						}

						return new PortfolioAnalysisParams(
							id,
							null,
							positions,
							isBenchmarkPerPortfolio ? associatedPortfolios.entities[id].description : null,
							currency,
							additionalAnalysisParams.statuses,
							isBenchmarkPerPortfolio ? additionalAnalysisParams?.rules?.[id] : additionalAnalysisParams?.rules
						);
					}));
			} else if (id.startsWith(`${WebUserDataConstants.SUBTYPE.ASSET_CLASS}:`)) {
				// if id is an asset class
				const globalId = id.replace(`${WebUserDataConstants.SUBTYPE.ASSET_CLASS}:`, '');

				const assetClassPositions = [new WweApiParamPositionImpl(globalId, { globalId }, 100, 100)];
				return of(new PortfolioAnalysisParams(id, null, assetClassPositions, null, currency));
			}
			else {
				throw new Error('Unknown portfolio type');
			}
		});
	}

	private createPortfolioBenchmarkApiParam(portfolioBenchmarkId: string, associatedPortfolios: any, modelsConfig: any, currency: string): Observable<ApiParamPortfolio> {
		const benchmarkPortfolio = associatedPortfolios.entities[portfolioBenchmarkId];
		let benchmarkPositions: WweBenchmarkPosition[];

		// model benchmark should not be flatten in the request
		if (modelsConfig?.[portfolioBenchmarkId]) {
			const model = associatedPortfolios.entities[portfolioBenchmarkId];
			const finalId = PortfolioIdUtil.isThirdPartyModel(portfolioBenchmarkId) && model ? model.tpmId : portfolioBenchmarkId;
			const modelRequestId = modelsConfig?.[finalId]?.aladdinPortfolioCode ?? finalId;

			const modelPositions = [new WweApiParamPositionImpl('benchmark-' + modelRequestId, { modelId: modelRequestId }, 100, 100)];
			return of(new ApiParamPortfolio(portfolioBenchmarkId, modelPositions, ListingCategories.MODEL_PORTFOLIO, currency));

		} else if (benchmarkPortfolio) {
			// check if the benchmark portfolio has the blend
			if (benchmarkPortfolio.children?.some(child => BlendPortfolioUtil.isChildPortfolio(child))) {
				return this.apiParamService.flattenPortfolios([portfolioBenchmarkId], this.storeConfig).pipe(
					first(),
					map(portfolioParams => {
						return new ApiParamPortfolio(
							portfolioBenchmarkId,
							PortfolioPositionsUtil.createWweParamPositions(portfolioParams[0]['positions'], 'benchmark-'),
							ListingCategories.USER_PORTFOLIO,
							currency
						);
					}));
			} else {
				benchmarkPositions = benchmarkPortfolio.children ?? [];
			}
		} else {
			throw new Error('Could not find Benchmark as Portfolio');
		}

		return of(new ApiParamPortfolio(
			portfolioBenchmarkId,
			PortfolioPositionsUtil.createWweParamPositions(benchmarkPositions, 'benchmark-'),
			ListingCategories.USER_PORTFOLIO,
			currency
		));
	}

	private resolveCurrency(currency: string): string {
		return this.configService.get<string>(SharedConfigKey.PC_CURRENCY_SELECTION_OPTIONS, NilePaConstants.DEFAULT_CURRENCIES).split(',').includes(currency) ? currency : null;
	}

	private buildPositionRequestFromIdentifier(position: any, securityKeyIdentifier: string) {
		return {
			id: PortfolioSecurityIdentifierMappingUtil.buildSecurityKey(position, securityKeyIdentifier),
			identifiers: PortfolioSecurityIdentifierMappingUtil.buildIdentifiersMap(position)
		} as SecurityIdentifiersMapRequest;
	}

	private mapPositionsViaSecurityKeyIdentifier(associatedPortfoliosPosition, portfolio, securityKeyIdentifier) {
		const associatedPositionRequestId =
			PortfolioSecurityIdentifierMappingUtil.buildSecurityKey(associatedPortfoliosPosition, securityKeyIdentifier);
		const securityFound = (portfolio?.children ?? []).find((position: WwePosition) => position?.requestId === associatedPositionRequestId);
		return securityFound;
	}
}

